import { z } from "zod";

/**
 * Organisation role (docs/users.md): `admin` sees and does everything;
 * `member` gets rights per project through project access.
 */
export const Role = z.enum(["admin", "member"]);
export type Role = z.infer<typeof Role>;

export const Locale = z.enum(["en", "ru"]);
export type Locale = z.infer<typeof Locale>;

export const PASSWORD_MIN_LENGTH = 12;
const Password = z.string().min(PASSWORD_MIN_LENGTH).max(256);
const Email = z.email().max(254);

/** The signed-in user as the web app sees it. */
export const Me = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  /** The panel's language: the browser's until the person chooses (`localeChosen`). */
  locale: Locale,
  /** The person chose the panel's language; until then the browser's language is used. */
  localeChosen: z.boolean(),
  role: Role,
  /** The admin who owns the organisation (created it or had it handed over). */
  isOrganizationOwner: z.boolean(),
  organizationId: z.uuid(),
  hasPassword: z.boolean(),
  passkeyCount: z.number().int().nonnegative(),
  totpEnabled: z.boolean(),
  /** Everyone must add a passkey or TOTP before using the panel. */
  secondFactorRequired: z.boolean(),
});
export type Me = z.infer<typeof Me>;

/** `PUT /me/locale` — the panel's language, chosen by the person (the language switcher). */
export const SetLocaleRequest = z.object({ locale: Locale });
export type SetLocaleRequest = z.infer<typeof SetLocaleRequest>;

/**
 * What the sign-in screen needs: whether the first account still has to be
 * created, and where the current session stands.
 */
export const AuthState = z.object({
  bootstrapNeeded: z.boolean(),
  /** The first-account form must carry the install's setup code. */
  bootstrapCodeRequired: z.boolean(),
  session: z.enum(["none", "needs_second_factor", "signed_in"]),
  me: Me.nullable(),
});
export type AuthState = z.infer<typeof AuthState>;

export const BootstrapRequest = z.object({
  organizationName: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(100),
  email: Email,
  /** Optional: an owner may go passkey-only. */
  password: Password.optional(),
  locale: Locale.default("en"),
  /** Required when the install sets a setup code (BOOTSTRAP_CODE). */
  setupCode: z.string().trim().max(256).optional(),
});
export type BootstrapRequest = z.infer<typeof BootstrapRequest>;

export const PasswordLoginRequest = z.object({
  email: Email,
  password: z.string().min(1).max(256),
});
export type PasswordLoginRequest = z.infer<typeof PasswordLoginRequest>;

export const CodeRequest = z.object({ code: z.string().trim().min(6).max(32) });
export type CodeRequest = z.infer<typeof CodeRequest>;

/** WebAuthn JSON passes through unchanged; the server library validates it. */
const WebAuthnJson = z.record(z.string(), z.unknown());

export const PasskeyOptions = z.object({
  challengeId: z.uuid(),
  options: WebAuthnJson,
});
export type PasskeyOptions = z.infer<typeof PasskeyOptions>;

export const PasskeySignInRequest = z.object({
  challengeId: z.uuid(),
  response: WebAuthnJson,
});
export type PasskeySignInRequest = z.infer<typeof PasskeySignInRequest>;

export const PasskeyRegisterRequest = PasskeySignInRequest.extend({
  name: z.string().trim().min(1).max(64),
});
export type PasskeyRegisterRequest = z.infer<typeof PasskeyRegisterRequest>;

export const Passkey = z.object({
  id: z.string(),
  name: z.string(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type Passkey = z.infer<typeof Passkey>;

export const TotpSetup = z.object({
  /** Base32 secret for manual entry. */
  secret: z.string(),
  /** otpauth:// URI for QR codes. */
  uri: z.string(),
});
export type TotpSetup = z.infer<typeof TotpSetup>;

/** Shown once; only hashes are stored. */
export const RecoveryCodes = z.object({ codes: z.array(z.string()) });
export type RecoveryCodes = z.infer<typeof RecoveryCodes>;
